import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { API, typeLabel } from "../api";

const TYPE_ICONS = { "CP": "🏖️", "RTT": "🕐", "Maladie": "🤒", "Maternite": "👶", "Paternite": "👨‍👶", "Pacs": "💍", "Sans solde": "📋" };
const typeIcon = (t) => TYPE_ICONS[t] || "📅";

function StatusBadge({ statut, cancelRequested }) {
  if (cancelRequested) return (
    <span style={{ background: "#FAEEDA", color: "#854F0B", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
      ⏳ Annulation en attente
    </span>
  );
  const cfg = {
    pending: { bg: "#FAEEDA", color: "#854F0B", label: "En attente", icon: "⏳" },
    approved: { bg: "#EAF3DE", color: "#3B6D11", label: "Approuvé", icon: "✅" },
    refused: { bg: "#FCEBEB", color: "#A32D2D", label: "Refusé", icon: "❌" },
  }[statut] || { bg: "#f0f0f0", color: "#888", label: statut, icon: "•" };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function LeaveCard({ l, isManager, onCancel, onRequestCancel }) {
  const debut = new Date(l.date_debut);
  const fin = new Date(l.date_fin);
  const sameMonth = debut.getMonth() === fin.getMonth() && debut.getFullYear() === fin.getFullYear();
  const dateStr = sameMonth
    ? `${debut.toLocaleDateString("fr-FR", { day: "2-digit" })} – ${fin.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}`
    : `${debut.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} – ${fin.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}`;

  const isPast = fin < new Date();
  const borderColor = l.statut === "approved" ? "#97C459" : l.statut === "refused" ? "#F09595" : "#F5C97A";

  return (
    <div style={{
      background: "#fff", borderRadius: 12, padding: "16px 20px",
      border: "1px solid #eee", borderLeft: `4px solid ${borderColor}`,
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      opacity: isPast && l.statut === "approved" ? 0.7 : 1,
      transition: "box-shadow 0.2s",
      display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
    }}>
      {/* Icône type */}
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: l.statut === "approved" ? "#EAF3DE" : l.statut === "refused" ? "#FCEBEB" : "#FAEEDA",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, flexShrink: 0,
      }}>
        {typeIcon(l.type)}
      </div>

      {/* Infos principales */}
      <div style={{ flex: 1, minWidth: 160 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>{typeLabel(l.type)}</span>
          <StatusBadge statut={l.statut} cancelRequested={l.cancel_requested && !isManager} />
          {isPast && <span style={{ fontSize: 10, color: "#bbb", background: "#f5f5f5", padding: "2px 7px", borderRadius: 10 }}>Passé</span>}
        </div>
        <div style={{ fontSize: 12, color: "#777", display: "flex", alignItems: "center", gap: 8 }}>
          <span>📅 {dateStr}</span>
          <span style={{ color: "#ddd" }}>•</span>
          <span style={{ fontWeight: 600, color: "#555" }}>{parseFloat(l.jours)} jour{parseFloat(l.jours) > 1 ? "s" : ""}</span>
        </div>
        {l.commentaire && (
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 4, fontStyle: "italic" }}>"{l.commentaire}"</div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        {l.statut === "pending" && (
          <button onClick={() => onCancel(l.id)}
            style={{ background: "#FCEBEB", color: "#A32D2D", border: "1px solid #F09595", borderRadius: 6, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
            Annuler
          </button>
        )}
        {l.statut === "approved" && isManager && !isPast && (
          <button onClick={() => onCancel(l.id)}
            style={{ background: "#FCEBEB", color: "#A32D2D", border: "1px solid #F09595", borderRadius: 6, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
            Annuler
          </button>
        )}
        {l.statut === "approved" && !isManager && !l.cancel_requested && !isPast && (
          <button onClick={() => onRequestCancel(l.id)}
            style={{ background: "#FAEEDA", color: "#854F0B", border: "1px solid #F5C97A", borderRadius: 6, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
            Demander annulation
          </button>
        )}
      </div>
    </div>
  );
}

function Historique() {
  const { token, user } = useAuth();
  const isManager = user.role === "associe" || user.role === "daf";
  const [leaves, setLeaves] = useState([]);
  const [notification, setNotification] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const loadLeaves = () => {
    setLoading(true);
    axios.get(API + "/leaves/mine", {
      headers: { Authorization: "Bearer " + token }
    }).then(r => { setLeaves(r.data.filter(l => l.type !== "Absence")); setLoading(false); });
  };

  useEffect(() => { loadLeaves(); }, [token]);

  const showNotif = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(""), 4000);
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Annuler cette demande ?")) return;
    try {
      await axios.delete(API + "/leaves/" + id,
        { headers: { Authorization: "Bearer " + token } }
      );
      loadLeaves();
      showNotif("✅ Demande annulée. Votre solde a été reconstitué.");
    } catch (err) { console.error(err); }
  };

  const handleRequestCancel = async (id) => {
    if (!window.confirm("Demander l'annulation de ce congé approuvé ? Un email sera envoyé à votre responsable.")) return;
    try {
      await axios.put(API + "/leaves/" + id + "/request-cancel", {},
        { headers: { Authorization: "Bearer " + token } }
      );
      loadLeaves();
      showNotif("✅ Demande d'annulation envoyée à votre responsable.");
    } catch (err) { console.error(err); }
  };

  const handleExportIcal = () => {
    window.open(API + "/leaves/ical?token=" + token, "_blank");
  };

  // Filtrage
  const filtered = leaves.filter(l => {
    if (filter === "all") return true;
    return l.statut === filter;
  });

  // Stats rapides
  const approved = leaves.filter(l => l.statut === "approved").length;
  const pending = leaves.filter(l => l.statut === "pending").length;
  const refused = leaves.filter(l => l.statut === "refused").length;
  const totalJours = leaves.filter(l => l.statut === "approved" && l.type === "CP").reduce((s, l) => s + parseFloat(l.jours || 0), 0);

  const FILTERS = [
    { key: "all", label: "Tous", count: leaves.length },
    { key: "approved", label: "Approuvés", count: approved },
    { key: "pending", label: "En attente", count: pending },
    { key: "refused", label: "Refusés", count: refused },
  ];

  // Grouper par année
  const byYear = {};
  filtered.forEach(l => {
    const y = new Date(l.date_debut).getFullYear();
    if (!byYear[y]) byYear[y] = [];
    byYear[y].push(l);
  });
  const years = Object.keys(byYear).sort((a, b) => b - a);

  return (
    <div style={{ maxWidth: 1000 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>📋 Mes congés</h2>
        <button onClick={handleExportIcal}
          style={{ background: "#E6F1FB", color: "#185FA5", border: "1px solid #85B7EB", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          📅 Exporter vers Outlook / Google
        </button>
      </div>

      {/* Stats rapides */}
      {!loading && leaves.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
          {[
            { label: "Total approuvés", value: totalJours + " j", color: "#3B6D11", bg: "#EAF3DE" },
            { label: "Total demandes", value: leaves.length, color: "#185FA5", bg: "#E6F1FB" },
            { label: "En attente", value: pending, color: "#854F0B", bg: "#FAEEDA" },
            { label: "Refusés", value: refused, color: refused > 0 ? "#A32D2D" : "#aaa", bg: refused > 0 ? "#FCEBEB" : "#f5f5f5" },
          ].map(k => (
            <div key={k.label} style={{ background: k.bg, borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 10, color: k.color, opacity: 0.8, marginTop: 2 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div style={{ background: "#EAF3DE", color: "#3B6D11", border: "1px solid #97C459", padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 500 }}>
          {notification}
        </div>
      )}

      {/* Filtres */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              padding: "5px 14px", borderRadius: 20, border: "1.5px solid", fontSize: 12, cursor: "pointer",
              fontWeight: filter === f.key ? 700 : 400,
              borderColor: filter === f.key ? "#7C6FE0" : "#e0e0e0",
              background: filter === f.key ? "#EEEDFE" : "#fff",
              color: filter === f.key ? "#7C6FE0" : "#666",
            }}>
            {f.label} {f.count > 0 && <span style={{ opacity: 0.7 }}>({f.count})</span>}
          </button>
        ))}
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ background: "#f5f5f5", borderRadius: 12, height: 72, animation: "pulse 1.4s ease-in-out infinite" }} />
          ))}
        </div>
      )}

      {/* Liste groupée par année */}
      {!loading && filtered.length === 0 && (
        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 40, textAlign: "center", color: "#aaa", fontSize: 13 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
          Aucune demande.
        </div>
      )}

      {!loading && years.map(year => (
        <div key={year} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{year}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {byYear[year].sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut)).map(l => (
              <LeaveCard key={l.id} l={l} isManager={isManager}
                onCancel={handleCancel} onRequestCancel={handleRequestCancel} />
            ))}
          </div>
        </div>
      ))}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

export default Historique;
